// Cross-links schema identities that hit the SAME commercial schema, so a folder of
// e.g. SugarCRM-shaped CSVs surfaces under the real SugarCRM database's identity.
// The folder attribution (`commercialMatch.matchedFolderPath` + `dataSourceId`) is
// computed by the worker at match time for file-source snapshots only.
// Pure functions — jest-tested.

import { getDataSourceNumericId, isFileDatasourceType } from '../DataSources/datasourcesApi';
import { normalizeFolderKey, folderLeafName } from './folderPathUtils';

/**
 * Folder-attributed commercial matches from OTHER identities that hit the same
 * commercial schema as the selected identity. One entry per distinct folder,
 * highest confidence wins, sorted by confidence descending.
 *
 * Returns [] when the selected identity has no commercial match of its own —
 * without a shared product there's nothing to cross-link on.
 */
export function relatedFolderMatches(identity, identities) {
  const cm = identity && identity.commercialMatch;
  if (!cm || cm.commercialSchemaId == null || !Array.isArray(identities)) return [];

  const byFolder = new Map();
  identities.forEach((other) => {
    if (!other || String(other.id) === String(identity.id)) return;
    const ocm = other.commercialMatch;
    if (!ocm || ocm.commercialSchemaId !== cm.commercialSchemaId) return;
    if (!ocm.matchedFolderPath) return;
    const member = (other.dataSources || []).find(
      (d) => String(d.dataSourceId) === String(ocm.dataSourceId)
    );
    const entry = {
      identityId: other.id,
      folderPath: ocm.matchedFolderPath,
      confidence: ocm.confidence || 0,
      confidenceLabel: ocm.confidenceLabel || null,
      vendorName: ocm.vendorName || null,
      productName: ocm.productName || null,
      sourceDataSourceId: ocm.dataSourceId != null ? String(ocm.dataSourceId) : null,
      sourceName: member ? member.name : null,
    };
    const key = normalizeFolderKey(entry.folderPath);
    const existing = byFolder.get(key);
    if (!existing || entry.confidence > existing.confidence) byFolder.set(key, entry);
  });

  return [...byFolder.values()].sort((a, b) => b.confidence - a.confidence);
}

/**
 * The file data source whose rootPath IS this folder, or null. Type-agnostic on
 * purpose: any file source rooted exactly at the folder counts as covering it —
 * the requirement is "a data source exists for the folder", not a specific format.
 */
export function coveringSource(folderPath, connections) {
  const key = normalizeFolderKey(folderPath);
  if (!key) return null;
  return (
    (connections || []).find((c) => {
      if (!c || !isFileDatasourceType(c.datasourceType)) return false;
      const rootPath = c.rootPath || (c.config && c.config.rootPath);
      return rootPath && normalizeFolderKey(rootPath) === key;
    }) || null
  );
}

/**
 * Prefill payload (pre-base64) for the source editor's `?prefill=` param, creating a
 * data source rooted at the matched folder. Platform (vfsScheme) and file type are
 * inherited from the source the profile ran against; a generic owner falls through
 * as FILE_GENERIC_FOLDER, which is always a safe way to scan a mixed folder.
 */
export function buildCreateSourcePrefill(match, connections) {
  const owner =
    (connections || []).find(
      (c) => String(getDataSourceNumericId(c)) === String(match.sourceDataSourceId)
    ) || null;
  return {
    type:
      owner && isFileDatasourceType(owner.datasourceType)
        ? owner.datasourceType
        : 'FILE_GENERIC_FOLDER',
    vfsScheme: (owner && owner.vfsScheme) || 'LOCAL',
    rootPath: match.folderPath,
    name: folderLeafName(match.folderPath),
  };
}
